import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router";
import SignIn from "./pages/AuthPages/SignIn";
import EsqueciSenha from "./pages/AuthPages/EsqueciSenha";
import RedefinirSenha from "./pages/AuthPages/RedefinirSenha";
import NotFound from "./pages/OtherPage/NotFound";
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./components/common/ScrollToTop";
import ProtectedRoute from "./routes/ProtectedRoute";
import { useConfig } from "./context/ConfigContext";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Liderancas = lazy(() => import("./pages/Liderancas"));
const Prospeccoes = lazy(() => import("./pages/Prospeccoes"));
const Votos = lazy(() => import("./pages/Votos"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const AnaliseVotacao = lazy(() => import("./pages/AnaliseVotacao"));
const MapaResultados = lazy(() => import("./pages/MapaResultados"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const ImportarBoletim = lazy(() => import("./pages/ImportarBoletim"));
const ImportarLocais = lazy(() => import("./pages/ImportarLocais"));
const ImportarPerfilEleitorado = lazy(() => import("./pages/ImportarPerfilEleitorado"));
const Candidatos = lazy(() => import("./pages/Candidatos"));
const Assessores = lazy(() => import("./pages/Assessores"));
const Mensagens = lazy(() => import("./pages/Mensagens"));
const Eventos = lazy(() => import("./pages/Eventos"));
const Admin = lazy(() => import("./pages/Admin"));
const Tarefas = lazy(() => import("./pages/Tarefas"));
const Geolocalizacao = lazy(() => import("./pages/Geolocalizacao"));
const Comunidade = lazy(() => import("./pages/Comunidade"));
const Investimentos = lazy(() => import("./pages/Investimentos"));
const Solicitacoes = lazy(() => import("./pages/Solicitacoes"));
const RegrasNegocio = lazy(() => import("./pages/RegrasNegocio"));
const FluxoComercial = lazy(() => import("./pages/FluxoComercial"));
const Diretorios = lazy(() => import("./pages/Diretorios"));
const TrocarSenha = lazy(() => import("./pages/TrocarSenha"));
const Acessos = lazy(() => import("./pages/Acessos"));
const AjudaEmbeds = lazy(() => import("./pages/AjudaEmbeds"));
const CadastroApoiador = lazy(() => import("./pages/CadastroApoiador"));
const Afiliados = lazy(() => import("./pages/Afiliados"));

const lazyRoute = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

export default function App() {
  const { loading } = useConfig();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-gray-900">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<SignIn />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/apoie/:token" element={lazyRoute(CadastroApoiador)} />
        {/* Fora do AppLayout (sem sidebar) — pra onde ProtectedRoute manda
            todo mundo com senha_provisoria=true, antes de liberar o resto. */}
        <Route path="/trocar-senha" element={<ProtectedRoute>{lazyRoute(TrocarSenha)}</ProtectedRoute>} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={lazyRoute(Dashboard)} />
          <Route path="/liderancas" element={lazyRoute(Liderancas)} />
          <Route path="/prospeccoes" element={lazyRoute(Prospeccoes)} />
          <Route path="/comunidade" element={lazyRoute(Comunidade)} />
          <Route
            path="/afiliados"
            element={
              <ProtectedRoute roles={["admin", "candidato", "assessor"]}>
                {lazyRoute(Afiliados)}
              </ProtectedRoute>
            }
          />
          <Route path="/diretorios" element={lazyRoute(Diretorios)} />
          <Route
            path="/regras-negocio"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(RegrasNegocio)}</ProtectedRoute>}
          />
          <Route
            path="/como-funciona"
            element={<ProtectedRoute roles={["admin", "candidato", "assessor"]}>{lazyRoute(FluxoComercial)}</ProtectedRoute>}
          />
          <Route
            path="/acessos"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(Acessos)}</ProtectedRoute>}
          />
          <Route
            path="/votos"
            element={
              <ProtectedRoute roles={["admin", "candidato"]}>{lazyRoute(Votos)}</ProtectedRoute>
            }
          />
          <Route
            path="/analise-votacao"
            element={
              <ProtectedRoute roles={["admin", "candidato"]}>
                {lazyRoute(AnaliseVotacao)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/mapa-resultados"
            element={
              <ProtectedRoute roles={["admin", "candidato"]}>
                {lazyRoute(MapaResultados)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/usuarios"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(Usuarios)}</ProtectedRoute>}
          />
          <Route
            path="/candidatos"
            element={<ProtectedRoute roles={["admin", "candidato"]}>{lazyRoute(Candidatos)}</ProtectedRoute>}
          />
          <Route
            path="/assessores"
            element={
              <ProtectedRoute roles={["admin", "candidato", "assessor"]}>
                {lazyRoute(Assessores)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/mensagens"
            element={
              <ProtectedRoute roles={["admin", "candidato", "lideranca", "assessor"]}>
                {lazyRoute(Mensagens)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/eventos"
            element={
              <ProtectedRoute roles={["admin", "candidato", "lideranca", "assessor"]}>
                {lazyRoute(Eventos)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/tarefas"
            element={
              <ProtectedRoute roles={["admin", "candidato", "lideranca", "assessor"]}>
                {lazyRoute(Tarefas)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda-candidato"
            element={<Navigate to="/eventos" replace />}
          />
          <Route
            path="/geolocalizacao"
            element={
              <ProtectedRoute roles={["admin", "candidato", "lideranca", "assessor"]}>
                {lazyRoute(Geolocalizacao)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/investimentos"
            element={
              <ProtectedRoute roles={["admin", "candidato", "assessor"]}>
                {lazyRoute(Investimentos)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/solicitacoes"
            element={
              <ProtectedRoute roles={["admin", "candidato", "lideranca", "assessor"]}>
                {lazyRoute(Solicitacoes)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/importar-boletim"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(ImportarBoletim)}</ProtectedRoute>}
          />
          <Route
            path="/importar-locais"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(ImportarLocais)}</ProtectedRoute>}
          />
          <Route
            path="/importar-perfil-eleitorado"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(ImportarPerfilEleitorado)}</ProtectedRoute>}
          />
          <Route
            path="/configuracoes"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(Configuracoes)}</ProtectedRoute>}
          />
          <Route
            path="/admin"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(Admin)}</ProtectedRoute>}
          />
          <Route
            path="/ajuda-embeds"
            element={<ProtectedRoute roles={["admin"]}>{lazyRoute(AjudaEmbeds)}</ProtectedRoute>}
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}
